import { createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  employeesService,
  EmployeeContract,
  ContractAuditLog,
} from '@/features/employees/services/employees.service';
import { ContractFormDialog } from '@/features/employees/components/ContractFormDialog';
import { ContractLogDetailsDialog } from '@/features/employees/components/ContractLogDetailsDialog';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft, Eye } from 'lucide-react';

const actionLabels: Record<string, string> = {
  create: 'Criado',
  update: 'Atualizado',
  delete: 'Removido',
};

const fieldLabels: Record<string, string> = {
  contractType: 'Tipo',
  parentContractId: 'Contrato Base',
  startDate: 'Data Início',
  endDate: 'Data Término',
  value: 'Valor',
  scope: 'Escopo',
  documentType: 'Tipo Doc.',
  storageKey: 'Arquivo',
};

function getChangesSummary(log: ContractAuditLog): string {
  const changedFields = log.changedFields || {};
  const fields = Object.keys(changedFields).filter((key) => key !== 'changeTypes');
  
  if (fields.length === 0) {
    return '-';
  }

  const summaries = fields
    .slice(0, 3)
    .map((field) => fieldLabels[field] || field)
    .join(', ');

  return fields.length > 3 ? `${summaries}...` : summaries;
}

export const Route = createFileRoute('/_authenticated/contracts/logs')({
  component: ContractLogsPage,
});

function ContractLogsPage() {
  const navigate = useNavigate();
  const routerState = useRouterState();
  const fromState = routerState.location.state as
    | { from?: string; fromLabel?: string }
    | undefined;
  const backPath = fromState?.from || '/contracts';
  const backLabel = fromState?.fromLabel || 'Gestão de Contratos';
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUserContracts, setSelectedUserContracts] = useState<EmployeeContract[]>([]);
  const [selectedLog, setSelectedLog] = useState<ContractAuditLog | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

  const { data: logs = [], isLoading } = useQuery({
    queryKey: ['contract-logs'],
    queryFn: employeesService.getContractLogs,
  });

  const { data: employees = [] } = useQuery({
    queryKey: ['employees-select'],
    queryFn: () => employeesService.listEmployeesForSelect(),
  });

  const createContractMutation = useMutation({
    mutationFn: ({
      userId,
      data,
      file,
    }: {
      userId: string;
      data: Parameters<typeof employeesService.createContract>[1];
      file: File;
    }) => employeesService.createContract(userId, data, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contract-logs'] });
      toast({ title: 'Contrato adicionado' });
    },
    onError: () => {
      toast({
        title: 'Erro ao adicionar contrato',
        variant: 'destructive',
      });
    },
  });

  const handleUserChange = async (userId: string) => {
    if (!userId) return;
    const contracts = await employeesService.listEmployeeContracts(userId);
    setSelectedUserContracts(contracts);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-sm text-muted-foreground">
            {backLabel} / Logs
          </div>
          <h1 className="text-3xl font-bold">Logs de Contratos</h1>
          <p className="text-muted-foreground">
            Histórico de alterações em contratos e aditivos.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => navigate({ to: backPath })}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Button onClick={() => setDialogOpen(true)}>Adicionar contrato</Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Registros</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-muted-foreground">Carregando logs...</div>
          ) : logs.length === 0 ? (
            <div className="text-muted-foreground">Nenhum log encontrado.</div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Prestador</TableHead>
                    <TableHead>Ação</TableHead>
                    <TableHead>Mudanças</TableHead>
                    <TableHead>Responsável</TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="font-medium">
                        {log.employeeFullName || '-'}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{actionLabels[log.action] || log.action}</Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {getChangesSummary(log)}
                        </span>
                      </TableCell>
                      <TableCell>{log.changedByEmail || '-'}</TableCell>
                      <TableCell>
                        {new Date(log.createdAt).toLocaleString('pt-BR')}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedLog(log);
                              setDetailsDialogOpen(true);
                            }}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            Ver detalhes
                          </Button>
                          {log.contractUserId && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                navigate({ to: `/employees/${log.contractUserId}` })
                              }
                            >
                              Ver prestador
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <ContractFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSubmit={async ({ userId, data, file }) => {
          await createContractMutation.mutateAsync({ userId, data, file });
        }}
        allowUserSelect
        users={employees.map((employee) => ({
          id: employee.id,
          label: employee.fullName || employee.email,
        }))}
        contracts={selectedUserContracts}
        onUserChange={handleUserChange}
      />

      <ContractLogDetailsDialog
        open={detailsDialogOpen}
        onOpenChange={setDetailsDialogOpen}
        log={selectedLog}
      />
    </div>
  );
}
